// Smart Daara - Landing Page
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  BookOpen, Users, BarChart3, Video, FileQuestion, Award,
  ClipboardCheck, MessageCircle, GraduationCap, CheckCircle,
  ArrowRight, Star, Phone, Globe, Mail, Volume2, VolumeX
} from 'lucide-react';

const LOGO_URL = '/logo.png';

export default function LandingPage() {
  const { t, language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [videoMuted, setVideoMuted] = useState(true);
  const heroVideoRef = useRef(null);

  const toggleVideoSound = () => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setVideoMuted(v.muted);
    if (!v.muted && v.paused) v.play().catch(() => {});
  };

  const getDashboardPath = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'super_admin': return '/admin';
      case 'school_admin':
      case 'manager': return '/school';
      case 'teacher': return '/teacher';
      case 'student': return '/student';
      case 'parent': return '/parent';
      default: return '/login';
    }
  };

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      icon: Video,
      title: language === 'fr' ? 'Cours en ligne' : 'Online courses',
      description: language === 'fr'
        ? 'Vidéos YouTube ou téléversées, PDF, sécurisés et diffusés en streaming.'
        : 'YouTube or uploaded videos, PDFs, secured and streamed.',
      color: 'bg-emerald-100 text-emerald-700',
    },
    {
      icon: BarChart3,
      title: language === 'fr' ? 'Suivi de progression' : 'Progress tracking',
      description: language === 'fr'
        ? 'Suivi automatique des leçons, pages PDF vues, temps passé, taux de complétion.'
        : 'Automatic tracking of lessons, viewed PDF pages, time spent, completion rate.',
      color: 'bg-blue-100 text-blue-700',
    },
    {
      icon: FileQuestion,
      title: language === 'fr' ? 'Évaluations & devoirs' : 'Assessments & quizzes',
      description: language === 'fr'
        ? 'Devoirs, quiz de passage (≥ 80% requis) qui débloquent les leçons suivantes.'
        : 'Quizzes and gateway assessments (≥ 80% required) that unlock next lessons.',
      color: 'bg-amber-100 text-amber-700',
    },
    {
      icon: Award,
      title: language === 'fr' ? 'Certificats' : 'Certificates',
      description: language === 'fr'
        ? 'Certificats officiels générés automatiquement à la complétion d\'un cours.'
        : 'Official certificates automatically generated on course completion.',
      color: 'bg-purple-100 text-purple-700',
    },
    {
      icon: Users,
      title: language === 'fr' ? 'Multi-rôles' : 'Multiple roles',
      description: language === 'fr'
        ? 'Admin, manager, enseignant, élève, parent — chacun sa vue personnalisée.'
        : 'Admin, manager, teacher, student, parent — each with a tailored view.',
      color: 'bg-rose-100 text-rose-700',
    },
    {
      icon: ClipboardCheck,
      title: language === 'fr' ? 'Gestion des inscriptions' : 'Enrollment management',
      description: language === 'fr'
        ? 'Inscription en lot par classe, validation, suivi des demandes par les enseignants.'
        : 'Bulk enrollment by class, validation, request tracking by teachers.',
      color: 'bg-teal-100 text-teal-700',
    },
    {
      icon: BookOpen,
      title: language === 'fr' ? 'Bibliothèque de cours' : 'Course library',
      description: language === 'fr'
        ? 'Programme organisé par classe → matière → cours → leçon → devoir.'
        : 'Curriculum organized by class → subject → course → lesson → quiz.',
      color: 'bg-indigo-100 text-indigo-700',
    },
    {
      icon: MessageCircle,
      title: language === 'fr' ? 'Paiement mobile' : 'Mobile payment',
      description: language === 'fr'
        ? 'Paiements Wave & Orange Money sécurisés (XOF), reversements aux parents.'
        : 'Secure Wave & Orange Money payments (XOF), payouts to parents.',
      color: 'bg-orange-100 text-orange-700',
    },
  ];

  const plans = [
    {
      name: language === 'fr' ? 'Débutant' : 'Starter',
      price: '30,000',
      period: language === 'fr' ? '/mois' : '/month',
      features: [
        '1 Admin',
        '6 Managers',
        language === 'fr' ? '12 Enseignants' : '12 Teachers',
        language === 'fr' ? '12 Niveaux' : '12 Levels',
        language === 'fr' ? '12 Matières / niveau' : '12 Subjects / level',
        language === 'fr' ? '24 Leçons / matière' : '24 Lessons / subject',
      ],
      popular: false,
    },
    {
      name: language === 'fr' ? 'Professionnel' : 'Pro',
      price: '60,000',
      period: language === 'fr' ? '/mois' : '/month',
      features: [
        '2 Admins',
        '6 Managers',
        language === 'fr' ? '30 Enseignants' : '30 Teachers',
        language === 'fr' ? '30 Niveaux' : '30 Levels',
        language === 'fr' ? '30 Matières / niveau' : '30 Subjects / level',
        language === 'fr' ? '50 Leçons / matière' : '50 Lessons / subject',
        language === 'fr' ? 'Portail Parents' : 'Parents Portal',
      ],
      popular: true,
    },
    {
      name: 'Premium',
      price: '100,000',
      period: language === 'fr' ? '/mois' : '/month',
      features: [
        '3 Admins',
        '6 Managers',
        language === 'fr' ? 'Enseignants illimités' : 'Unlimited teachers',
        language === 'fr' ? 'Niveaux illimités' : 'Unlimited levels',
        language === 'fr' ? 'Matières illimitées' : 'Unlimited subjects',
        language === 'fr' ? 'Leçons illimitées' : 'Unlimited lessons',
        language === 'fr' ? 'Support 24/7' : '24/7 Support',
      ],
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen bg-white font-jakarta" data-testid="landing-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-amber-50/30" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-emerald-600 rounded-full blur-3xl" />
          <div className="absolute top-40 right-40 w-48 h-48 bg-amber-500 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full text-emerald-700 text-sm font-medium">
                <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                {language === 'fr' ? 'Plateforme E-Learning #1 au Sénégal' : '#1 E-Learning Platform in Senegal'}
              </div>

              <div>
                <h1 className="font-outfit text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight tracking-tight">
                  Smart Daara
                </h1>
                <p className="mt-3 font-outfit text-2xl sm:text-3xl text-emerald-700 font-semibold">
                  {language === 'fr' ? 'L\'école en ligne' : 'The online school'}
                </p>
              </div>

              <p className="text-lg text-slate-600 max-w-lg leading-relaxed">
                {language === 'fr'
                  ? 'La plateforme e-learning complète pour digitaliser votre école : cours en ligne, suivi de progression, évaluations, certificats et paiements mobiles.'
                  : 'The complete e-learning platform to digitize your school: online courses, progress tracking, assessments, certificates and mobile payments.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                {isAuthenticated ? (
                  <Button
                    size="lg"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white px-8"
                    onClick={() => navigate(getDashboardPath())}
                    data-testid="hero-dashboard-btn"
                  >
                    {t('dashboard')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                ) : (
                  <>
                    <Button
                      size="lg"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white px-8"
                      onClick={scrollToContact}
                      data-testid="hero-cta-btn"
                    >
                      {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-slate-300 text-slate-700"
                      onClick={() => navigate('/login')}
                      data-testid="hero-login-btn"
                    >
                      {t('login')}
                    </Button>
                  </>
                )}
              </div>

              <div className="flex gap-8 pt-8 border-t border-slate-200">
                <div>
                  <p className="text-3xl font-outfit font-bold text-slate-900">50+</p>
                  <p className="text-sm text-slate-500">{language === 'fr' ? 'Écoles' : 'Schools'}</p>
                </div>
                <div>
                  <p className="text-3xl font-outfit font-bold text-slate-900">5,000+</p>
                  <p className="text-sm text-slate-500">{language === 'fr' ? 'Étudiants' : 'Students'}</p>
                </div>
                <div>
                  <p className="text-3xl font-outfit font-bold text-slate-900">1,000+</p>
                  <p className="text-sm text-slate-500">{language === 'fr' ? 'Cours' : 'Courses'}</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  ref={heroVideoRef}
                  src="https://smart-daara.b-cdn.net/site/smart_daara_hero_v2.mp4"
                  className="w-full h-auto object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  data-testid="hero-video"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                <button
                  onClick={toggleVideoSound}
                  className="absolute bottom-4 right-4 z-10 w-11 h-11 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                  aria-label={videoMuted ? 'Activer le son' : 'Couper le son'}
                  title={videoMuted ? 'Activer le son' : 'Couper le son'}
                  data-testid="hero-video-sound-btn"
                >
                  {videoMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">
                    {language === 'fr' ? 'Progression suivie' : 'Progress Tracked'}
                  </p>
                  <p className="text-sm text-slate-500">
                    {language === 'fr' ? 'En temps réel' : 'Real-time'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Fonctionnalités' : 'Features'}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Tout ce dont votre école a besoin pour enseigner, évaluer et faire progresser vos élèves.'
                : 'Everything your school needs to teach, assess and grow your students.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="border-0 shadow-sm hover:shadow-md transition-shadow bg-white"
                data-testid={`feature-card-${index}`}
              >
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg ${feature.color} flex items-center justify-center mb-4`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-lg font-outfit">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Tarifs' : 'Pricing'}
            </h2>
            <p className="text-lg text-slate-600">
              {language === 'fr'
                ? 'Des plans adaptés à toutes les tailles d\'école'
                : 'Plans adapted to all school sizes'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`relative ${plan.popular ? 'border-2 border-emerald-600 shadow-lg' : 'border shadow-sm'}`}
                data-testid={`pricing-card-${index}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-600 text-white text-sm font-medium rounded-full">
                    {language === 'fr' ? 'Plus populaire' : 'Most Popular'}
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl font-outfit">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                    <span className="text-slate-500"> FCFA{plan.period}</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-8 ${plan.popular ? 'bg-emerald-700 hover:bg-emerald-800' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={scrollToContact}
                    data-testid={`pricing-cta-${index}`}
                  >
                    {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold mb-4">
              {language === 'fr' ? 'Contactez-nous' : 'Contact us'}
            </h2>
            <p className="text-emerald-100 text-lg max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Prêt à digitaliser votre école ? Notre équipe DKR Consulting est à votre écoute pour créer votre compte et vous accompagner.'
                : 'Ready to digitize your school? Our DKR Consulting team is here to set up your account and support you.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <a
              href="tel:+221778312002"
              className="flex flex-col items-center p-6 bg-white/10 hover:bg-white/20 rounded-xl backdrop-blur-sm transition-colors"
              data-testid="contact-phone-sn"
            >
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
                <Phone className="w-6 h-6" />
              </div>
              <p className="text-xs uppercase tracking-wide text-emerald-100 mb-1">Sénégal</p>
              <p className="font-semibold text-lg">+221 77 831 20 02</p>
            </a>

            <a
              href="tel:+33619446736"
              className="flex flex-col items-center p-6 bg-white/10 hover:bg-white/20 rounded-xl backdrop-blur-sm transition-colors"
              data-testid="contact-phone-fr"
            >
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
                <Phone className="w-6 h-6" />
              </div>
              <p className="text-xs uppercase tracking-wide text-emerald-100 mb-1">France</p>
              <p className="font-semibold text-lg">+33 6 19 44 67 36</p>
            </a>

            <a
              href="https://dkr-consulting.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-6 bg-white/10 hover:bg-white/20 rounded-xl backdrop-blur-sm transition-colors"
              data-testid="contact-website"
            >
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
                <Globe className="w-6 h-6" />
              </div>
              <p className="text-xs uppercase tracking-wide text-emerald-100 mb-1">
                {language === 'fr' ? 'Site web' : 'Website'}
              </p>
              <p className="font-semibold text-lg">dkr-consulting.com</p>
            </a>
          </div>

          <div className="text-center mt-12 pt-8 border-t border-white/20">
            <p className="text-emerald-100 text-sm">
              {language === 'fr'
                ? 'Powered by '
                : 'Powered by '}
              <a
                href="https://dkr-consulting.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-semibold hover:underline"
              >
                DKR Consulting
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Smart Daara" className="h-10 w-auto" />
              <span className="font-outfit font-bold text-xl text-white">Smart Daara</span>
            </div>
            <p className="text-sm">
              © 2026 Smart Daara. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
